// Generic, schema-driven form renderer for the study's final form.
// Handles regular fields, nested groups, slot-driven repeat groups, gates
// (conditional visibility), and tables. Decoupled from any specific form.
import React from "react";
import { useVisibilityTelemetry } from "./telemetry";

export function DynamicForm({
  schema,
  value,
  onChange,
  onTelemetry,
  telemetrySurface = "form",
  onFieldFocus,
  onFieldInput,
  onFieldBlur,
  fieldAnnotations = {},
}) {
  const setField = (id, v) => onChange({ ...value, [id]: v });
  const tracking = {
    onTelemetry,
    surface: telemetrySurface,
    onFieldFocus,
    onFieldInput,
    onFieldBlur,
    fieldAnnotations,
  };
  return (
    <div className="dform">
      {schema.map((q) => (
        <SchemaItem key={q.id} q={q} record={value} setField={setField} tracking={tracking} />
      ))}
    </div>
  );
}

function SchemaItem({ q, record, setField, tracking }) {
  switch (q.structure_type) {
    case "group":
      return (
        <GroupBlock
          q={q}
          value={record[q.id] || {}}
          onChange={(v) => setField(q.id, v)}
          tracking={tracking}
        />
      );
    case "repeat_group":
      return (
        <RepeatBlock
          q={q}
          record={record}
          value={record[q.id]}
          setField={setField}
          tracking={tracking}
        />
      );
    case "gate": {
      const on = record[q.gate.gate_on];
      const open = (q.gate.when_values || []).includes(on);
      if (!open) return null;
      return (
        <div className="dform-gate">
          {q.label && <div className="dform-gate-label">{q.label}</div>}
          {q.fields.map((f) => (
            <FieldRow
              key={f.id}
              q={f}
              fieldId={f.id}
              value={record[f.id]}
              onChange={(v) => setField(f.id, v)}
              tracking={tracking}
            />
          ))}
        </div>
      );
    }
    case "regular":
    default:
      return (
        <FieldRow
          q={q}
          fieldId={q.id}
          value={record[q.id]}
          onChange={(v) => setField(q.id, v)}
          tracking={tracking}
        />
      );
  }
}

function GroupBlock({ q, value, onChange, tracking }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="dform-group">
      {q.label && <div className="dform-group-label">{q.label}</div>}
      {q.fields.map((f) => (
        <FieldRow
          key={f.id}
          q={f}
          fieldId={f.id}
          value={value[f.id]}
          onChange={(v) => set(f.id, v)}
          tracking={tracking}
        />
      ))}
    </div>
  );
}

function RepeatBlock({ q, record, value, setField, tracking }) {
  const items = Array.isArray(value) ? value : [];
  const fromSlot = q.repeat?.from_slot;
  const slotCount = parseInt(fromSlot ? record[fromSlot] : NaN, 10);
  const slotDriven = Number.isFinite(slotCount) && slotCount > 0;
  const count = slotDriven ? slotCount : Math.max(items.length, 1);

  const display = [...items];
  while (display.length < count) display.push({});
  const finalItems = display.slice(0, count);

  const fieldId = (i, k) => `${q.id}[${i + 1}].${k}`;
  const fieldValue = (it, i, k) => record[fieldId(i, k)] ?? it[k];
  const setItem = (i, k, v) => setField(fieldId(i, k), v);
  const add = () => setField(q.id, [...items, {}]);
  const remove = (i) => setField(q.id, items.filter((_, idx) => idx !== i));
  const itemLabel = (i) =>
    (q.repeat?.item_label || "Item {{index}}").replace("{{index}}", i + 1);

  return (
    <div className="dform-group">
      {q.label && <div className="dform-group-label">{q.label}</div>}
      {finalItems.map((it, i) => (
        <div className="dform-card" key={i}>
          <div className="dform-card-title">
            <span>{itemLabel(i)}</span>
            {!slotDriven && finalItems.length > 1 && (
              <button type="button" className="dform-x" onClick={() => remove(i)}>
                Remove
              </button>
            )}
          </div>
          {q.fields.map((f) => {
            const id = fieldId(i, f.id);
            return (
              <FieldRow
                key={f.id}
                q={f}
                fieldId={id}
                value={fieldValue(it, i, f.id)}
                onChange={(v) => setItem(i, f.id, v)}
                tracking={tracking}
              />
            );
          })}
        </div>
      ))}
      {!slotDriven && (
        <button type="button" className="btn ghost sm" onClick={add}>
          + Add another
        </button>
      )}
    </div>
  );
}

function TableBlock({ q, fieldId, value, onChange, tracking }) {
  const rows = Array.isArray(value) ? value : [];
  const setCell = (i, k, v) => {
    const next = rows.slice();
    next[i] = { ...(next[i] || {}), [k]: v };
    onChange(next);
  };
  const add = () => onChange([...rows, {}]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="dform-field">
      <label className="dform-label">{q.question_text}</label>
      {rows.map((row, i) => (
        <div className="dform-card" key={i}>
          <div className="dform-card-title">
            <span>Row {i + 1}</span>
            <button type="button" className="dform-x" onClick={() => remove(i)}>
              Remove
            </button>
          </div>
          {(q.columns || []).map((c) => (
            <FieldRow
              key={c.id}
              q={c}
              fieldId={`${fieldId}[${i + 1}].${c.id}`}
              value={row[c.id]}
              onChange={(v) => setCell(i, c.id, v)}
              tracking={tracking}
            />
          ))}
        </div>
      ))}
      <button type="button" className="btn ghost sm" onClick={add}>
        + Add row
      </button>
    </div>
  );
}

function FieldRow({ q, fieldId, value, onChange, tracking }) {
  const annotation = tracking?.fieldAnnotations?.[fieldId] || null;
  const annotationClass = annotation
    ? ` has-review-annotation ${annotation.tone || ""}`
    : "";
  const visibilityRef = useVisibilityTelemetry({
    onTelemetry: tracking?.onTelemetry,
    eventPrefix: "field",
    context: {
      field_id: fieldId,
      surface: tracking?.surface || "form",
    },
  });
  const handleChange = (nextValue) => {
    tracking?.onFieldInput?.(fieldId, value, nextValue);
    onChange(nextValue);
  };

  if (q.type === "table") {
    return (
      <div className={`dform-field${annotationClass}`} ref={visibilityRef} data-field-id={fieldId}>
        {annotation && <FieldAnnotation annotation={annotation} />}
        <TableBlock
          q={q}
          fieldId={fieldId}
          value={value}
          onChange={handleChange}
          tracking={tracking}
        />
      </div>
    );
  }
  return (
    <div className={`dform-field${annotationClass}`} ref={visibilityRef} data-field-id={fieldId}>
      <label className="dform-label">{q.question_text || q.label}</label>
      {annotation && <FieldAnnotation annotation={annotation} />}
      <FieldInput
        q={q}
        value={value}
        onChange={handleChange}
        onFocus={() => tracking?.onFieldFocus?.(fieldId, value)}
        onBlur={(currentValue) => tracking?.onFieldBlur?.(
          fieldId,
          currentValue === undefined ? value : currentValue
        )}
      />
    </div>
  );
}

function FieldAnnotation({ annotation }) {
  const lines = Array.isArray(annotation.lines) ? annotation.lines : [annotation.text].filter(Boolean);
  return (
    <div className={`final-review-annotation ${annotation.tone || ""}`} role="note">
      {lines.map((line) => <span key={line}>{line}</span>)}
    </div>
  );
}

function FieldInput({ q, value, onChange, onFocus, onBlur }) {
  const t = q.type;

  if (t === "single_choice") {
    return (
      <select
        className="ef"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={(e) => onBlur(e.currentTarget.value)}
      >
        <option value="" disabled>
          Select…
        </option>
        {(q.options || []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }

  if (t === "multiple_choice") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className="dform-choices">
        {(q.options || []).map((o) => (
          <button
            type="button"
            key={o}
            className={`chip ${arr.includes(o) ? "on" : ""}`}
            onClick={() => toggle(o)}
            onFocus={onFocus}
            onBlur={() => onBlur(arr)}
          >
            {o}
          </button>
        ))}
      </div>
    );
  }

  if (t === "bool") {
    return (
      <div className="dform-choices">
        <button
          type="button"
          className={`chip ${value === true ? "on" : ""}`}
          onClick={() => onChange(true)}
          onFocus={onFocus}
          onBlur={() => onBlur(value)}
        >
          Yes
        </button>
        <button
          type="button"
          className={`chip ${value === false ? "on" : ""}`}
          onClick={() => onChange(false)}
          onFocus={onFocus}
          onBlur={() => onBlur(value)}
        >
          No
        </button>
      </div>
    );
  }

  if (t === "multiline_text") {
    return (
      <textarea
        className="ef ef-area"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={(e) => onBlur(e.currentTarget.value)}
      />
    );
  }

  const inputType = t === "number" ? "number" : t === "date" ? "date" : t === "time" ? "time" : "text";
  return (
    <input
      className="ef"
      type={inputType}
      value={value ?? ""}
      onChange={(e) =>
        onChange(inputType === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)
      }
      onFocus={onFocus}
      onBlur={(e) => onBlur(
        inputType === "number"
          ? (e.currentTarget.value === "" ? "" : Number(e.currentTarget.value))
          : e.currentTarget.value
      )}
    />
  );
}
